import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CarFront, KeyRound, BadgeDollarSign, ConciergeBell, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n";
import Reveal, { MaskedLines } from "@/components/Reveal";
import { Slider } from "@/components/ui/slider";

const HERO_IMG = "https://images.unsplash.com/photo-1681167816895-940c56e0d2a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjaGF1ZmZldXIlMjBkcml2ZXIlMjBleGVjdXRpdmUlMjBjYXIlMjB2ZWhpY2xlJTIwcmVudGFsfGVufDB8fHx8MTc5MzYyOTQ5MXww&ixlib=rb-4.1.0&q=85";
const COCKPIT_IMG = "https://images.unsplash.com/photo-1432462770865-65b70566d673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjaGF1ZmZldXIlMjBkcml2ZXIlMjBleGVjdXRpdmUlMjBjYXIlMjB2ZWhpY2xlJTIwcmVudGFsfGVufDB8fHx8MTc5MzYyOTQ5MXww&ixlib=rb-4.1.0&q=85";
const SHOWROOM_IMG = "https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjaGF1ZmZldXIlMjBkcml2ZXIlMjBleGVjdXRpdmUlMjBjYXIlMjB2ZWhpY2xlJTIwcmVudGFsfGVufDB8fHx8MTc5MzYyOTQ5MXww&ixlib=rb-4.1.0&q=85";
const ALPINE_IMG = "https://images.unsplash.com/photo-1586376087814-7080e39b82da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxTd2lzcyUyMGFscHMlMjBsdXh1cnklMjBleGVjdXRpdmUlMjBzZWRhbiUyMGNhcnxlbnwwfHx8fDE3OTM2Mjk0OTd8MA&ixlib=rb-4.1.0&q=85";
const SUV_IMG = "https://images.unsplash.com/photo-1506244856291-8910ea843e81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxTd2lzcyUyMGFscHMlMjBsdXh1cnklMjBleGVjdXRpdmUlMjBzZWRhbiUyMGNhcnxlbnwwfHx8fDE3OTM2Mjk0OTd8MA&ixlib=rb-4.1.0&q=85";

const SERVICE_ICONS = [CarFront, KeyRound, BadgeDollarSign, ConciergeBell];
const ROUTES_KM = [35, 280, 150, 460, 150];
const VEHICLE_RATE = [2.8, 3.6, 3.2];
const VEHICLE_DAY = [290, 240, 350, 220];

const chf = (n) => `${Math.round(n / 5) * 5}`.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

const Select = ({ testid, label, value, onChange, options, light }) => (
  <div>
    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{label}</label>
    <div className="relative">
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-4 py-3 pr-10 text-sm text-slate-900 outline-none transition-colors focus:border-red-500"
      >
        {options.map((o, i) => (
          <option key={o} value={i}>{o}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  </div>
);

const TransportCalculator = () => {
  const { t } = useLanguage();
  const c = t.transport.calculator;
  const [service, setService] = useState(0);
  const [route, setRoute] = useState(1);
  const [vehicle, setVehicle] = useState(0);
  const [days, setDays] = useState(3);

  const price = useMemo(() => {
    if (service === 0) return 80 + ROUTES_KM[route] * VEHICLE_RATE[vehicle];
    if (service === 1) return VEHICLE_DAY[vehicle] * days;
    return null;
  }, [service, route, vehicle, days]);

  return (
    <section data-testid="calculator-transport-container" className="px-4 py-24 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] sm:p-10">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">{c.title}</p>
            <p className="mt-2 max-w-lg text-sm text-slate-500">{c.desc}</p>
            <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div className="space-y-6">
                <Select testid="calc-service-select" label={c.serviceLabel} value={service} onChange={setService} options={c.services} light />
                {service === 0 && (
                  <Select testid="calc-route-select" label={c.routeLabel} value={route} onChange={setRoute} options={c.routes} light />
                )}
                <Select testid="calc-vehicle-select" label={c.vehicleLabel} value={vehicle} onChange={setVehicle} options={c.vehicles} light />
                {service === 1 && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{c.daysLabel}</label>
                      <span className="font-mono text-sm font-bold text-red-600" data-testid="calc-days-value">{days} {c.days}</span>
                    </div>
                    <Slider data-testid="calc-days-slider" min={1} max={30} step={1} value={[days]} onValueChange={([v]) => setDays(v)} />
                  </div>
                )}
              </div>
              <div className="flex flex-col justify-center rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">{c.result}</p>
                <p data-testid="calc-result" className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {price === null ? c.onRequest : `CHF ${chf(price)}`}
                </p>
                <p className="mt-4 text-xs text-slate-400">{c.note}</p>
                <Link
                  to="/contact"
                  data-testid="calc-cta-btn"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-600"
                >
                  {c.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const TransportPage = () => {
  const { t } = useLanguage();
  const span = ["col-span-12 md:col-span-7", "col-span-12 md:col-span-5", "col-span-12 md:col-span-5", "col-span-12 md:col-span-7"];
  const imgs = [COCKPIT_IMG, null, SHOWROOM_IMG, null];

  return (
    <main data-testid="section-pole-transport" className="bg-[#F8FAFC]">
      <section className="relative flex min-h-[92svh] items-end overflow-hidden px-4 pb-16 pt-32 sm:px-6">
        <img src={HERO_IMG} alt="Chauffeur privé et berline de luxe" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F8FAFC] via-[#F8FAFC]/60 to-[#0F172A]/30" />
        <div className="relative z-10 mx-auto w-full max-w-7xl">
          <MaskedLines baseDelay={0.1} lines={[t.transport.badge]} lineClassName="font-mono text-xs uppercase tracking-[0.3em] text-red-600" />
          <MaskedLines
            baseDelay={0.25}
            lines={[t.transport.hero1, t.transport.hero2]}
            className="mt-5"
            lineClassName="text-4xl font-bold italic leading-[1.05] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl"
          />
          <Reveal delay={0.7}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">{t.transport.heroSub}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#calculator" data-testid="transport-hero-cta-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-600">
                {t.transport.cta1}
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/contact" data-testid="transport-hero-secondary-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-900/30 px-7 py-3.5 text-sm font-bold text-slate-900 transition-colors duration-200 hover:border-red-600 hover:text-red-600">
                {t.transport.cta2}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t.transport.servicesTitle}
            </h2>
            <p className="mt-3 text-sm text-slate-500 sm:text-base">{t.transport.servicesSub}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
            {t.transport.services.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={s.t} delay={i * 0.08} className={span[i]}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-red-500/40 hover:shadow-[0_24px_50px_-25px_rgba(15,23,42,0.3)] sm:p-9">
                    {imgs[i] && (
                      <>
                        <img src={imgs[i]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.08] transition-opacity duration-500 group-hover:opacity-[0.16]" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/85 to-transparent" />
                      </>
                    )}
                    <div className="relative">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900">
                        <Icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="mt-5 text-xl font-bold text-slate-900 sm:text-2xl">{s.t}</h3>
                      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-500">{s.d}</p>
                      {i === 1 && (
                        <Link
                          to="/transport/location"
                          data-testid="service-rental-link"
                          className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-red-600 transition-colors hover:text-red-500"
                        >
                          {t.transport.fleetCta}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 pb-8 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t.transport.fleetTitle}
                </h2>
                <p className="mt-3 text-sm text-slate-500 sm:text-base">{t.transport.fleetSub}</p>
              </div>
              <Link
                to="/transport/location"
                data-testid="fleet-cta-btn"
                className="inline-flex items-center gap-2 rounded-full border border-slate-900/30 px-6 py-3 text-sm font-bold text-slate-900 transition-colors duration-200 hover:border-red-600 hover:text-red-600"
              >
                {t.transport.fleetCta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/transport/location" data-testid="fleet-preview-link" className="group mt-10 block overflow-hidden rounded-3xl border border-slate-200 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="overflow-hidden">
                  <img
                    src={ALPINE_IMG}
                    alt={t.transport.fleetTitle}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col justify-center p-8 sm:p-12">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-red-600">{t.transport.fleetCta}</p>
                  <div className="mt-5 space-y-3">
                    {t.transport.calculator.vehicles.map((v) => (
                      <p key={v} className="flex items-center gap-3 text-base font-semibold text-slate-900">
                        <CarFront className="h-5 w-5 text-slate-400" strokeWidth={1.5} /> {v}
                      </p>
                    ))}
                  </div>
                  <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-red-600">
                    {t.transport.fleetCta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </p>
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <div id="calculator" className="scroll-mt-20">
        <TransportCalculator />
      </div>

      <section className="px-4 pb-24 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="rounded-3xl bg-slate-900 p-10 text-center sm:p-16">
              <h2 className="text-2xl font-bold tracking-tight text-white sm:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t.transport.ctaTitle}
              </h2>
              <p className="mx-auto mt-4 max-w-md text-sm text-slate-400 sm:text-base">{t.transport.ctaSub}</p>
              <Link
                to="/contact"
                data-testid="transport-cta-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-red-600 px-8 py-4 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-500"
              >
                {t.transport.ctaBtn}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default TransportPage;
